import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Wallet,
  ArrowRight,
  User,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchWithAuth } from '../utils/api';
import '../styles/responsive/pages/WalletChangeRequests.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const WalletChangeRequests = () => {
  const { token } = useSelector((state) => state.auth);

  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, cancelled: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showActionModal, setShowActionModal] = useState(null);
  const [actionData, setActionData] = useState({ rejectionReason: '', notes: '' });
  const [actionLoading, setActionLoading] = useState(false);

  const [statusFilter, setStatusFilter] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchRequests = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
      });

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await fetchWithAuth(`${API_BASE_URL}/user-details/admin/wallet-change-requests?${params}`);
      const data = await response.json();

      if (data.success) {
        setRequests(data.data.requests);
        setTotalPages(data.data.pagination.totalPages);
        setTotalItems(data.data.pagination.total);
        if (data.data.stats) {
          setStats(data.data.stats);
        }
      }
    } catch (error) {
      console.error('Failed to fetch wallet change requests:', error);
      toast.error('Failed to fetch wallet change requests');
    } finally {
      setIsLoading(false);
    }
  }, [token, currentPage, pageSize, statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async () => {
    if (!selectedRequest) return;

    setActionLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/user-details/admin/wallet-change-requests/${selectedRequest.requestId}/approve`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({ notes: actionData.notes }),
        }
      );

      const data = await response.json();

      if (data.success) {
        toast.success('Wallet change request approved successfully');
        setShowActionModal(null);
        setSelectedRequest(null);
        setActionData({ rejectionReason: '', notes: '' });
        fetchRequests();
      } else {
        toast.error(data.message || 'Failed to approve request');
      }
    } catch (error) {
      toast.error('Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !actionData.rejectionReason) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/user-details/admin/wallet-change-requests/${selectedRequest.requestId}/reject`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            rejectionReason: actionData.rejectionReason,
            notes: actionData.notes,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        toast.success('Wallet change request rejected');
        setShowActionModal(null);
        setSelectedRequest(null);
        setActionData({ rejectionReason: '', notes: '' });
        fetchRequests();
      } else {
        toast.error(data.message || 'Failed to reject request');
      }
    } catch (error) {
      toast.error('Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="rd-badge warn">Pending</span>;
      case 'approved':
        return <span className="rd-badge ok">Approved</span>;
      case 'rejected':
        return <span className="rd-badge err">Rejected</span>;
      case 'cancelled':
        return <span className="rd-badge plain">Cancelled</span>;
      default:
        return <span className="rd-badge plain">{status}</span>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateAddress = (address) => {
    if (!address) return '—';
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  return (
    <div className="wallet-change-requests-page">
      <div className="rd-page-header">
        <div>
          <h1 className="rd-page-title">Wallet Change Requests</h1>
          <p className="rd-page-subtitle">Manage merchant wallet address change requests</p>
        </div>
      </div>

      <div className="stats-grid">
        <div
          className={`rd-kpi stat-card clickable ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
        >
          <div className="rd-icon-tile stat-icon all">
            <Wallet size={20} />
          </div>
          <div className="stat-content">
            <span className="rd-kpi-value stat-value">{stats.pending + stats.approved + stats.rejected}</span>
            <span className="rd-kpi-label stat-label">All</span>
          </div>
        </div>
        <div
          className={`rd-kpi stat-card clickable ${statusFilter === 'pending' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('pending'); setCurrentPage(1); }}
        >
          <div className="rd-icon-tile stat-icon pending">
            <Clock size={20} />
          </div>
          <div className="stat-content">
            <span className="rd-kpi-value stat-value">{stats.pending}</span>
            <span className="rd-kpi-label stat-label">Pending</span>
          </div>
        </div>
        <div
          className={`rd-kpi stat-card clickable ${statusFilter === 'approved' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('approved'); setCurrentPage(1); }}
        >
          <div className="rd-icon-tile stat-icon success">
            <CheckCircle size={20} />
          </div>
          <div className="stat-content">
            <span className="rd-kpi-value stat-value">{stats.approved}</span>
            <span className="rd-kpi-label stat-label">Approved</span>
          </div>
        </div>
        <div
          className={`rd-kpi stat-card clickable ${statusFilter === 'rejected' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('rejected'); setCurrentPage(1); }}
        >
          <div className="rd-icon-tile stat-icon error">
            <XCircle size={20} />
          </div>
          <div className="stat-content">
            <span className="rd-kpi-value stat-value">{stats.rejected}</span>
            <span className="rd-kpi-label stat-label">Rejected</span>
          </div>
        </div>
        <div
          className={`rd-kpi stat-card clickable ${statusFilter === 'cancelled' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('cancelled'); setCurrentPage(1); }}
        >
          <div className="rd-icon-tile stat-icon cancelled">
            <X size={20} />
          </div>
          <div className="stat-content">
            <span className="rd-kpi-value stat-value">{stats.cancelled}</span>
            <span className="rd-kpi-label stat-label">Cancelled</span>
          </div>
        </div>
      </div>

      <div className="rd-table-wrap table-container">
        {isLoading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="rd-empty empty-state">
            <div className="rd-empty-icon"><Wallet size={20} /></div>
            <h3>No wallet change requests</h3>
            <p>There are no {statusFilter !== 'all' ? statusFilter : ''} wallet change requests at the moment.</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <table className="rd-table data-table">
              <thead>
                <tr>
                  <th>Request ID</th>
                  <th>Merchant</th>
                  <th>Current Address</th>
                  <th>New Address</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.requestId}>
                    <td className="mono">{request.requestId}</td>
                    <td>
                      <div className="merchant-info">
                        <User size={14} />
                        <span>{request.merchantName || `Merchant #${request.merchantId}`}</span>
                      </div>
                    </td>
                    <td className="mono" title={request.currentAddress}>
                      {truncateAddress(request.currentAddress)}
                    </td>
                    <td className="mono" title={request.newAddress}>
                      {truncateAddress(request.newAddress)}
                    </td>
                    <td className="reason-cell">
                      {request.reason ? (
                        <span title={request.reason}>
                          {request.reason.length > 30 ? request.reason.slice(0, 30) + '...' : request.reason}
                        </span>
                      ) : '—'}
                    </td>
                    <td>{getStatusBadge(request.status)}</td>
                    <td>{formatDate(request.createdAt)}</td>
                    <td>
                      {request.status === 'pending' ? (
                        <div className="action-buttons">
                          <button
                            className="btn-approve"
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowActionModal('approve');
                            }}
                          >
                            <Check size={14} />
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowActionModal('reject');
                            }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile card list — hidden on desktop via CSS */}
            <div className="request-card-list">
              {requests.map((request) => (
                <div key={request.requestId} className="request-row-card">
                  <div className="request-row-card-header">
                    <div className="request-row-card-merchant">
                      <User size={14} />
                      <span>{request.merchantName || `Merchant #${request.merchantId}`}</span>
                    </div>
                    {getStatusBadge(request.status)}
                  </div>
                  <div className="request-row-card-body">
                    <div className="card-field">
                      <span className="card-field-label">Request ID</span>
                      <span className="card-field-value mono">{request.requestId}</span>
                    </div>
                    <div className="card-field">
                      <span className="card-field-label">Current Address</span>
                      <span className="card-field-value mono" title={request.currentAddress}>
                        {truncateAddress(request.currentAddress)}
                      </span>
                    </div>
                    <div className="card-field">
                      <span className="card-field-label">New Address</span>
                      <span className="card-field-value mono" title={request.newAddress}>
                        {truncateAddress(request.newAddress)}
                      </span>
                    </div>
                    {request.reason && (
                      <div className="card-field">
                        <span className="card-field-label">Reason</span>
                        <span className="card-field-value">{request.reason}</span>
                      </div>
                    )}
                  </div>
                  <div className="request-row-card-footer">
                    <span className="card-date">{formatDate(request.createdAt)}</span>
                    {request.status === 'pending' ? (
                      <div className="card-actions">
                        <button
                          className="btn-approve"
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowActionModal('approve');
                          }}
                        >
                          <Check size={16} />
                        </button>
                        <button
                          className="btn-reject"
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowActionModal('reject');
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(1)}
          >
            <ChevronLeft size={16} />
            <ChevronLeft size={16} />
          </button>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            <ChevronLeft size={16} />
          </button>
          <span className="page-info">
            Page {currentPage} of {totalPages} ({totalItems} total)
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            <ChevronRight size={16} />
          </button>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >
            <ChevronRight size={16} />
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {showActionModal === 'approve' && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowActionModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Approve Wallet Change</h3>
              <button className="modal-close" onClick={() => setShowActionModal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="request-summary">
                <div className="summary-row">
                  <span className="label">Merchant:</span>
                  <span className="value">{selectedRequest.merchantName}</span>
                </div>
                <div className="summary-row address-change">
                  <div className="address-box">
                    <span className="address-label">Current Address</span>
                    <span className="address-value mono">{selectedRequest.currentAddress || 'None'}</span>
                  </div>
                  <ArrowRight size={20} className="arrow-icon" />
                  <div className="address-box new">
                    <span className="address-label">New Address</span>
                    <span className="address-value mono">{selectedRequest.newAddress}</span>
                  </div>
                </div>
                {selectedRequest.reason && (
                  <div className="summary-row">
                    <span className="label">Reason:</span>
                    <span className="value">{selectedRequest.reason}</span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Admin Notes (Optional)</label>
                <textarea
                  value={actionData.notes}
                  onChange={(e) => setActionData({ ...actionData, notes: e.target.value })}
                  placeholder="Add any internal notes..."
                  rows={3}
                />
              </div>

              <div className="warning-box">
                <AlertCircle size={20} />
                <p>This will update the merchant's wallet address immediately. Make sure the new address is correct.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="rd-btn rd-btn-secondary" onClick={() => setShowActionModal(null)}>
                Cancel
              </button>
              <button
                className="rd-btn rd-btn-primary"
                onClick={handleApprove}
                disabled={actionLoading}
              >
                {actionLoading ? 'Approving...' : 'Approve Change'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showActionModal === 'reject' && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowActionModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Wallet Change</h3>
              <button className="modal-close" onClick={() => setShowActionModal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="request-summary">
                <div className="summary-row">
                  <span className="label">Merchant:</span>
                  <span className="value">{selectedRequest.merchantName}</span>
                </div>
                <div className="summary-row">
                  <span className="label">Requested Address:</span>
                  <span className="value mono">{selectedRequest.newAddress}</span>
                </div>
              </div>

              <div className="form-group">
                <label>Rejection Reason *</label>
                <textarea
                  value={actionData.rejectionReason}
                  onChange={(e) => setActionData({ ...actionData, rejectionReason: e.target.value })}
                  placeholder="Please provide a reason for rejection..."
                  rows={3}
                  required
                />
              </div>

              <div className="form-group">
                <label>Admin Notes (Optional)</label>
                <textarea
                  value={actionData.notes}
                  onChange={(e) => setActionData({ ...actionData, notes: e.target.value })}
                  placeholder="Add any internal notes..."
                  rows={2}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="rd-btn rd-btn-secondary" onClick={() => setShowActionModal(null)}>
                Cancel
              </button>
              <button
                className="rd-btn wcr-btn-danger"
                onClick={handleReject}
                disabled={actionLoading || !actionData.rejectionReason}
              >
                {actionLoading ? 'Rejecting...' : 'Reject Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .wallet-change-requests-page { padding: 0; }

  /* Stats grid — clickable KPI filter cards (composed on .rd-kpi) */
  .wallet-change-requests-page .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
  }

  .wallet-change-requests-page .rd-kpi.stat-card {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 20px;
  }

  .wallet-change-requests-page .stat-card.clickable {
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;
  }

  .wallet-change-requests-page .stat-card.clickable:hover {
    border-color: var(--line-strong);
    box-shadow: var(--sh-md);
  }

  .wallet-change-requests-page .stat-card.clickable.active {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-soft);
  }

  .wallet-change-requests-page .stat-icon { flex-shrink: 0; }
  .wallet-change-requests-page .stat-icon.pending { background: var(--warn-soft); color: var(--warn); }
  .wallet-change-requests-page .stat-icon.success { background: var(--ok-soft); color: var(--ok); }
  .wallet-change-requests-page .stat-icon.error { background: var(--err-soft); color: var(--err); }
  .wallet-change-requests-page .stat-icon.cancelled { background: var(--bg-inset); color: var(--ink-muted); }

  .wallet-change-requests-page .stat-content { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .wallet-change-requests-page .rd-kpi-value.stat-value { margin-top: 0; font-size: 22px; }
  .wallet-change-requests-page .rd-kpi-label.stat-label { margin-top: 2px; }

  /* Table cells */
  .wallet-change-requests-page .data-table .mono { font-family: var(--font-mono); font-size: 13px; }
  .wallet-change-requests-page .merchant-info { display: flex; align-items: center; gap: 8px; }
  .wallet-change-requests-page .merchant-info svg { color: var(--ink-muted); }
  .wallet-change-requests-page .reason-cell {
    max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }

  /* Approve / reject icon buttons */
  .wallet-change-requests-page .action-buttons { display: flex; gap: 8px; }
  .wallet-change-requests-page .btn-approve,
  .wallet-change-requests-page .btn-reject {
    width: 32px; height: 32px; border-radius: var(--r-sm);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: background 0.15s, color 0.15s;
  }
  .wallet-change-requests-page .btn-approve {
    background: var(--ok-soft); border: 1px solid transparent; color: var(--ok);
  }
  .wallet-change-requests-page .btn-approve:hover { background: var(--ok); color: #fff; }
  .wallet-change-requests-page .btn-reject {
    background: var(--err-soft); border: 1px solid transparent; color: var(--err);
  }
  .wallet-change-requests-page .btn-reject:hover { background: var(--err); color: #fff; }

  /* Loading / empty */
  .wallet-change-requests-page .loading-state {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 60px 20px; color: var(--ink-secondary);
  }
  .wallet-change-requests-page .spinner {
    width: 32px; height: 32px; border: 3px solid var(--line);
    border-top-color: var(--accent); border-radius: 50%; animation: spin 1s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Pagination */
  .wallet-change-requests-page .pagination {
    display: flex; align-items: center; justify-content: center; gap: 8px; padding: 16px;
  }
  .wallet-change-requests-page .pagination button {
    display: flex; align-items: center; padding: 8px;
    background: var(--bg-surface); border: 1px solid var(--line-strong);
    border-radius: var(--r-sm); cursor: pointer; color: var(--ink-secondary); transition: all 0.15s;
  }
  .wallet-change-requests-page .pagination button:hover:not(:disabled) { border-color: var(--ink); color: var(--ink); }
  .wallet-change-requests-page .pagination button:disabled { opacity: 0.5; cursor: not-allowed; }
  .wallet-change-requests-page .page-info { font-size: 14px; color: var(--ink-secondary); padding: 0 16px; }

  /* Modal */
  .wallet-change-requests-page .modal-overlay {
    position: fixed; inset: 0; background: rgba(24, 24, 27, 0.45);
    display: flex; align-items: center; justify-content: center; z-index: 1000;
  }
  .wallet-change-requests-page .modal {
    background: var(--bg-surface); border: 1px solid var(--line); border-radius: var(--r-lg);
    width: 100%; max-width: 520px; max-height: 90vh; overflow: auto; box-shadow: var(--sh-pop);
  }
  .wallet-change-requests-page .modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 20px 24px; border-bottom: 1px solid var(--line);
  }
  .wallet-change-requests-page .modal-header h3 { margin: 0; font-size: 17px; font-weight: 700; color: var(--ink); }
  .wallet-change-requests-page .modal-close {
    background: none; border: none; padding: 4px; cursor: pointer; color: var(--ink-muted);
  }
  .wallet-change-requests-page .modal-close:hover { color: var(--ink); }
  .wallet-change-requests-page .modal-body { padding: 24px; }
  .wallet-change-requests-page .modal-footer {
    display: flex; justify-content: flex-end; gap: 12px; padding: 16px 24px; border-top: 1px solid var(--line);
  }

  /* Request summary */
  .wallet-change-requests-page .request-summary {
    background: var(--bg-subtle); border: 1px solid var(--line);
    border-radius: var(--r-md); padding: 16px; margin-bottom: 20px;
  }
  .wallet-change-requests-page .summary-row { display: flex; gap: 12px; padding: 8px 0; }
  .wallet-change-requests-page .summary-row:not(:last-child) { border-bottom: 1px solid var(--line); }
  .wallet-change-requests-page .summary-row .label { font-size: 13px; color: var(--ink-secondary); min-width: 100px; }
  .wallet-change-requests-page .summary-row .value { font-size: 14px; color: var(--ink); word-break: break-all; }
  .wallet-change-requests-page .summary-row .value.mono { font-family: var(--font-mono); font-size: 13px; }

  .wallet-change-requests-page .address-change { flex-direction: column; gap: 12px; align-items: center; }
  .wallet-change-requests-page .address-box {
    width: 100%; padding: 12px; background: var(--bg-surface);
    border: 1px solid var(--line-strong); border-radius: var(--r-sm);
  }
  .wallet-change-requests-page .address-box.new { border-color: var(--ok); background: var(--ok-soft); }
  .wallet-change-requests-page .address-label {
    display: block; font-size: 11px; font-weight: 600; color: var(--ink-muted);
    text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 4px;
  }
  .wallet-change-requests-page .address-value {
    font-size: 13px; color: var(--ink); word-break: break-all; font-family: var(--font-mono);
  }
  .wallet-change-requests-page .arrow-icon { color: var(--ink-muted); }

  /* Form */
  .wallet-change-requests-page .form-group { margin-bottom: 16px; }
  .wallet-change-requests-page .form-group label {
    display: block; font-size: 13px; font-weight: 600; color: var(--ink); margin-bottom: 8px;
  }
  .wallet-change-requests-page .form-group textarea {
    width: 100%; padding: 11px 13px; background: var(--bg-surface);
    border: 1px solid var(--line-strong); border-radius: var(--r-sm);
    font-size: 14px; color: var(--ink); resize: vertical; font-family: inherit;
    transition: border-color 0.13s, box-shadow 0.13s;
  }
  .wallet-change-requests-page .form-group textarea:focus {
    outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft);
  }

  /* Warning callout */
  .wallet-change-requests-page .warning-box {
    display: flex; gap: 12px; padding: 12px;
    background: var(--warn-soft); border: 1px solid var(--warn); border-radius: var(--r-sm);
  }
  .wallet-change-requests-page .warning-box svg { color:; flex-shrink: 0; }
  .wallet-change-requests-page .warning-box p { margin: 0; font-size: 13px; color: var(--ink-secondary); }

  /* Page-scoped danger button (Reject) */
  .wallet-change-requests-page .wcr-btn-danger { background: var(--err); color: #fff; }
  .wallet-change-requests-page .wcr-btn-danger:hover:not(:disabled) { background: var(--err-ink); }

  .wallet-change-requests-page .text-muted { color: var(--ink-muted); }
`;

export default WalletChangeRequests;
